import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const ACTION_COLOR = {
  "garbage_purge.email_rows": "bg-rose-50 text-rose-700 border-rose-200",
  "garbage_purge.test_brokerages": "bg-rose-50 text-rose-700 border-rose-200",
  "brokerage.normalize": "bg-blue-50 text-blue-700 border-blue-200",
  "phone.dedupe": "bg-amber-50 text-amber-700 border-amber-200",
  "name.fix": "bg-violet-50 text-violet-700 border-violet-200",
  "ignore.add": "bg-slate-100 text-slate-700 border-slate-300",
  "ignore.restore": "bg-sky-50 text-sky-700 border-sky-200",
};

export default function CleanupLog() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/cleanup/log?limit=200`);
      setEntries(data.entries || []);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5" data-testid="cleanup-log-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <Link
            to="/cleanup"
            className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900"
            data-testid="cleanup-log-back"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Back to Cleanup
          </Link>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 mt-2">Cleanup audit log</h1>
          <p className="text-sm text-slate-500 mt-1">Every cleanup action, newest first.</p>
        </div>
        <Button
          variant="outline"
          onClick={load}
          disabled={loading}
          className="min-h-[44px]"
          data-testid="cleanup-log-refresh"
        >
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {entries.length === 0 ? (
        <Card className="p-8 text-center text-slate-500">
          {loading ? "Loading…" : "No cleanup actions yet."}
        </Card>
      ) : (
        <div className="space-y-2">
          {entries.map((e) => (
            <Card key={e.id} className="p-4" data-testid={`log-entry-${e.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={ACTION_COLOR[e.action] || ""}>
                    {e.action}
                  </Badge>
                  <span className="text-xs text-slate-500">
                    by {e.actor_email}
                  </span>
                </div>
                <span className="text-xs text-slate-500">
                  {new Date(e.timestamp).toLocaleString()}
                </span>
              </div>
              {e.note && <div className="text-sm text-slate-600 mt-2">{e.note}</div>}
              <details className="mt-2">
                <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">
                  Show before/after ({(e.target_ids || []).length} target IDs)
                </summary>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                  <pre className="text-xs bg-slate-50 p-2 rounded-md overflow-x-auto max-h-60 border border-slate-200">
                    {JSON.stringify(e.before, null, 2)}
                  </pre>
                  <pre className="text-xs bg-emerald-50 p-2 rounded-md overflow-x-auto max-h-60 border border-emerald-200">
                    {JSON.stringify(e.after, null, 2)}
                  </pre>
                </div>
              </details>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
